export const PRINT_SECTION_ID = 'print-section';
export const WITH_PRINTING_CLASS = 'with-printing';

/**
 * Draws a 2D copy of the canvases from the originalEle to the cloned newEle
 * It will match elements by their data attribute, selecting elements in the
 * newEle children, based on the canvas data in the originalEle children
 *
 * @param originalEle - The original element with canvas items
 * @param newEle - The target cloned element without canvas items
 */
export function copyCanvases(originalEle: Element, newEle: Element): void {
    Array.from(originalEle.children).forEach((child, index) => {
        if (child instanceof HTMLCanvasElement) {
            // If we are a canvas, try to copy
            const oldCanvas = child;
            // Ignore empty canvases
            if (!(oldCanvas.width <= 0 || oldCanvas.height <= 0)) {
                // Select the same child index on the newEle
                const newCanvas = newEle.children[index];
                // If it exists

                if (newCanvas) {
                    const newCanvasHtml = newCanvas as HTMLCanvasElement;
                    newCanvasHtml.getContext('2d')?.drawImage(oldCanvas, 0, 0);
                } else {
                    // Otherwise add a helpful message
                    // eslint-disable-next-line no-console
                    console.error('Cannot find matching canvas element on cloned node.', {
                        index,
                        newEle,
                    });
                }
            }
        } else if (child.hasChildNodes()) {
            // Iterate on children for this child node
            copyCanvases(child, newEle.children[index]);
        }
    });
}

export interface PrintElementOptions {
    cleanAfterPrint?: boolean;
    cloneCanvases?: boolean;
    debug?: boolean;
    moveDiv?: boolean;
    onAfterPrint?: () => void;
    onBeforePrint?: () => void;
    printDelay?: number;
    setPrintPageWidth?: string;
}

/**
 * Opens a print window for the selected element, will only print a cloned
 * copy of the element, will not modify or try to print the original.
 * Warning: This can cause problems with canvases, if there is an issue, update
 * the copyCanvases function above.
 *
 * @param element - The element to clone for a print
 * @param options - The options to pass for print function
 * @param options.cleanAfterPrint - Should we clean the generated hidden print div after printing or before?
 * (defaults to true)
 * @param options.cloneCanvases - Should we also clone canvases within the element (defaults to true)
 * @param options.printDelay - Should there be a delay on the printing
 * @param options.debug - Use this for debugging, wont print but will prepare everything
 */
export function printElement(element: Element, options?: PrintElementOptions): void {
    const {
        cleanAfterPrint = true,
        cloneCanvases = true,
        debug = false,
        moveDiv = false,
        onAfterPrint,
        onBeforePrint,
        printDelay,
    } = options ?? {};

    const parentElem = element.parentElement;

    if (!parentElem) {
        throw new Error('The element must be added to the dom before printing');
    }

    let printSection = document.getElementById(PRINT_SECTION_ID);

    // If the element does not already exist, create and append it to the body
    if (!printSection) {
        printSection = document.createElement('div');
        printSection.id = PRINT_SECTION_ID;
        document.body.appendChild(printSection);
    }

    if (moveDiv) {
        printSection.appendChild(element);
    }

    function cloneAndPrint() {
        if (!printSection) {
            return;
        }

        onBeforePrint?.();

        // Clones the element you want to print
        const domClone = element.cloneNode(true);
        if (cloneCanvases) {
            // The html cloneNode does not clone canvases, run this hack to clone the canvas
            copyCanvases(element, domClone as Element);
        }
        if (!cleanAfterPrint) {
            // If we aren't cleaning after the print, we clean before
            printSection.innerHTML = '';
        }
        // Append the entire cloned dom
        printSection.appendChild(domClone);

        if (moveDiv) {
            // Move the old element back to the parent
            parentElem?.appendChild(element);
        }

        /*
         *  Add the 'with-printing' class to the body to prevent all other items showing for the print
         *  This will make it so when you click the print button, everything on the page will be hidden
         *  except the print section. Then if you are not clicking the print button, you can still print
         *  the page as normal.
         */
        document.body.classList.add(WITH_PRINTING_CLASS);

        function printWithCallback() {
            // If we are debugging the print, we do not run the print dialog or the after print
            if (debug) {
                return;
            }

            // Once the printing is complete we need to clean the printSection and remove the class from the body
            function afterPrint() {
                window.removeEventListener('afterprint', afterPrint);

                printSection = document.getElementById(PRINT_SECTION_ID);
                document.body.classList.remove(WITH_PRINTING_CLASS);
                if (printSection && cleanAfterPrint) {
                    printSection.innerHTML = '';
                }

                onAfterPrint?.();
            }

            window.addEventListener('afterprint', afterPrint);

            // Call the window to ask for a print
            window.print();
        }

        // If we need to wait, otherwise just call it
        if (printDelay) {
            setTimeout(printWithCallback, printDelay);
        } else {
            printWithCallback();
        }
    }

    // If we need to wait, otherwise just call it
    if (printDelay) {
        setTimeout(cloneAndPrint, printDelay);
    } else {
        cloneAndPrint();
    }
}
